/**
 * The first image URL in a list that actually loads.
 *
 * Supplier photo CDNs rot — seven of the fifteen URLs on the seeded rooms 404 at Sabre's CDN — and an
 * entity usually has several photos, so the fix is almost always "try the next one" rather than "give
 * up". Anywhere with room for a gallery does that by dropping the dead page (`CoverCarousel`); anywhere
 * with room for one frame uses this. The RN counterpart is `mobile/src/ui/first-available-image.tsx`,
 * a component rather than a hook because every native call site renders an `AppImage`.
 *
 * The first candidate is returned immediately rather than after a check, so the common case — a URL
 * that works — paints with no delay and no flash; stepping only happens once one has actually failed.
 *
 * `probe` is for call sites that cannot report a failure themselves. A CSS `background-image` has no
 * error event, so a dead URL there is a silently blank box; passing `probe` loads the URL through a
 * detached `Image` to find out. The browser serves the real background from cache afterwards, so it
 * costs a cache hit rather than a second download. An `<img>` call site should leave it off and wire
 * the returned `onError` instead — that is the same information without the extra request.
 */
import { useEffect, useState } from 'react';

export interface FirstAvailableImage {
  /** The candidate to render now, or `undefined` once every one has failed. */
  src: string | undefined;
  /** Wire to an `<img>`'s `onError` to step to the next candidate. */
  onError: () => void;
}

export function useFirstAvailableImage(
  candidates: Array<string | undefined>,
  { probe = false }: { probe?: boolean } = {},
): FirstAvailableImage {
  // De-duplicated because a URL repeated across the list would burn two steps on one dead photo.
  const urls = [...new Set(candidates.filter((url): url is string => !!url))];
  const [failedCount, setFailedCount] = useState(0);
  const src = urls[failedCount];
  const onError = () => setFailedCount((count) => count + 1);

  // A new set of candidates deserves a fresh start — otherwise a recycled row (a virtualised list
  // reusing this component for a different booking) carries the previous item's failures across.
  const key = urls.join('|');
  useEffect(() => setFailedCount(0), [key]);

  useEffect(() => {
    if (!probe || !src) return;
    const image = new Image();
    let cancelled = false;
    image.addEventListener('error', () => {
      if (!cancelled) setFailedCount((count) => count + 1);
    });
    image.src = src;
    // The listener outlives the effect otherwise: an unmounted row's probe would still resolve and set
    // state on a component that is gone.
    return () => {
      cancelled = true;
    };
  }, [probe, src]);

  return { src, onError };
}
